import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import PageHero from "@/components/bakery/PageHero";
import { RegisterArt } from "@/components/bakery/Art";
import Dropzone from "@/components/import/Dropzone";
import PreviewView from "@/components/import/PreviewView";
import MappingDialog from "@/components/common/MappingDialog";
import TxnDetailDialog from "@/components/common/TxnDetailDialog";
import { ChannelChip, VoidBadge } from "@/components/common/Chips";
import { DateField } from "@/components/common/Field";
import { Card, OutlineBtn, PrimaryBtn } from "@/components/common/ui";
import { api, downloadFile, errMsg } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { fmtDate, shiftDate, todayJkt } from "@/lib/format";
import { cn } from "@/lib/utils";

const STEPS = ["Tanggal Rekap", "Upload File", "Preview", "Commit"];

function Stepper({ step }) {
  return (
    <ol className="mb-8 grid grid-cols-4 gap-2" data-testid="import-stepper">
      {STEPS.map((s, i) => (
        <li key={s} className={cn("border-t-4 pt-2 text-[11px] font-semibold uppercase tracking-wider sm:text-xs", i <= step ? "border-brand text-ink" : "border-zinc-200 text-zinc-400")}>
          <span className="num mr-1">{i + 1}.</span>{s}
        </li>
      ))}
    </ol>
  );
}

function History({ onOpen, rk }) {
  const { data } = useApi("/import-history", { rk });
  if (!data?.length) return null;
  return (
    <div className="mt-12">
      <h2 className="mb-3 font-display text-lg font-bold">Riwayat Import</h2>
      <div className="overflow-x-auto rounded-xl border border-zinc-200 bg-white">
        <table className="dense-table w-full" data-testid="import-history">
          <thead><tr><th>No. Transaksi</th><th>Tanggal Rekap</th><th>Channel</th><th className="text-right">Order</th><th className="text-right">Baris</th><th className="text-right">Qty</th><th>File</th></tr></thead>
          <tbody>{data.map((t) => (
            <tr key={t.id} className="cursor-pointer" onClick={() => onOpen(t.id)}>
              <td className="num font-semibold text-brand underline-offset-2 hover:underline">{t.txn_no} <VoidBadge txn={t} /></td><td>{fmtDate(t.recap_date)}</td><td><ChannelChip channel={t.channel} /></td>
              <td className="num text-right">{t.order_count}</td><td className="num text-right">{t.line_count}</td><td className="num text-right">{t.lines.reduce((s, l) => s + l.qty, 0)}</td>
              <td className="max-w-[220px] truncate">{(t.source_files || []).join(", ")}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

export default function ImportMarketplace() {
  const navigate = useNavigate();
  const [recap, setRecap] = useState(shiftDate(todayJkt(), -1));
  const [files, setFiles] = useState([]);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mapRow, setMapRow] = useState(null);
  const [detail, setDetail] = useState(null);
  const [rk, setRk] = useState(0);
  const { data: products } = useApi("/products");
  const step = result ? 3 : preview ? 2 : files.length ? 1 : 0;

  const process = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("recap_date", recap);
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/imports/preview", fd);
      setPreview(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const refresh = async () => setPreview((await api.get(`/imports/${preview.id}`)).data);
  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/imports/${preview.id}/commit`);
      setResult(data); setRk((x) => x + 1);
      toast.success("Import berhasil di-commit ke Kartu Stok");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const reset = () => { setFiles([]); setPreview(null); setResult(null); };

  return (
    <div data-testid="import-page">
      <PageHero testid="import-hero" eyebrow="Kasir · Admin" title="Import Marketplace" Art={RegisterArt} compact
        desc="Rekap penjualan Shopee & Tokopedia/TikTok per tanggal pengiriman. Preview dulu, mapping yang belum cocok, lalu commit." />
      <Stepper step={step} />
      {result ? (
        <Card className="border-2 border-ink" data-testid="import-result">
          <div className="flex items-center gap-2 font-display text-xl font-bold"><CheckCircle2 className="h-6 w-6 text-brand" /> Import {fmtDate(recap)} tersimpan</div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {result.transactions.map((t) => (
              <button key={t.id} onClick={() => setDetail(t.id)} className="lift rounded-lg border border-zinc-200 p-4 text-left" data-testid={`import-result-${t.platform}`}>
                <div className="flex items-center justify-between"><ChannelChip channel={t.channel} /><span className="num text-xs font-bold">{t.txn_no}</span></div>
                <div className="mt-2 text-sm">+{t.new_lines} baris baru · {t.new_qty} qty · {t.order_count} order total</div>
              </button>
            ))}
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <PrimaryBtn onClick={() => navigate("/kartu-stok")} data-testid="import-go-kartu">Lihat Kartu Stok</PrimaryBtn>
            <OutlineBtn onClick={reset} data-testid="import-again-btn">Import lagi</OutlineBtn>
          </div>
        </Card>
      ) : preview ? (
        <PreviewView p={preview} committing={busy} onMap={(u) => setMapRow(u)} onCommit={commit} onBack={() => setPreview(null)}
          onExport={() => downloadFile(`/imports/${preview.id}/export`, {}, `preview-import-${recap}.xlsx`).catch((e) => toast.error(errMsg(e)))} />
      ) : (
        <div className="space-y-6">
          <div className="max-w-xs"><DateField label="Tanggal rekap" value={recap} onChange={setRecap} testid="import-recap-date" /></div>
          <Dropzone files={files} setFiles={setFiles} />
          <PrimaryBtn onClick={process} disabled={!files.length || busy} data-testid="import-process-btn">{busy ? "Memproses…" : "Proses Preview"}</PrimaryBtn>
        </div>
      )}
      <History onOpen={setDetail} rk={rk} />
      <MappingDialog open={Boolean(mapRow)} initial={mapRow && { platform: mapRow.platform, raw_name: mapRow.raw_name, raw_variation: mapRow.raw_variation }}
        products={products} onClose={() => setMapRow(null)} onSaved={async () => { setMapRow(null); await refresh(); }} />
      <TxnDetailDialog txnId={detail} onClose={() => setDetail(null)} onChanged={() => setRk((x) => x + 1)} />
    </div>
  );
}
